import { Controller } from "@hotwired/stimulus"
import { mapService } from "@/services/map_service"
import { terraDrawService } from "@/services/terra_draw_service"
import MapInitializationService from "@/services/map_initialization_service"
import PolygonService from "@/services/polygon_service"
import { errorHandler } from "@/services/error_handler_service"

// Import types
import type { Feature } from "geojson"
import type { GeoJSONStoreFeatures } from "terra-draw"
import type maplibregl from "maplibre-gl"

export default class extends Controller {
  static override values = {
    lat: Number,
    lng: Number,
    gardenId: Number,
    initialGeoJson: String
  }

  declare readonly latValue: number;
  declare readonly lngValue: number;
  declare readonly gardenIdValue: number;
  declare readonly initialGeoJsonValue: string;
  declare readonly hasLatValue: boolean;
  declare readonly hasLngValue: boolean;
  declare readonly hasGardenIdValue: boolean;
  declare readonly hasInitialGeoJsonValue: boolean;

  private map!: maplibregl.Map;
  private boundFinishHandler = (id: string | number, _context: unknown) => {
    this.handlePolygonFinish(id)
  }

  override async connect(): Promise<void> {
    try {
      await mapService.load()
      await this.initMap()
    } catch (error) {
      console.error('Failed to load map service:', error)
      this.showErrorMessage('Failed to load map. Please refresh the page.')
    }
  }

  private async initMap(): Promise<void> {
    // Calculate map center using service
    const center = MapInitializationService.calculateMapCenter({
      ...(this.hasLatValue && { explicitLat: this.latValue }),
      ...(this.hasLngValue && { explicitLng: this.lngValue }),
      ...(this.hasInitialGeoJsonValue && { geoJson: this.initialGeoJsonValue })
    });

    const mapTypeId = mapService.getMapTypeId()

    // Create map
    this.map = await mapService.createMap(this.element.querySelector('#property-map') as HTMLElement, {
      center,
      zoom: 18,
      mapTypeId: mapTypeId.TERRAIN,
      mapTypeControl: true
    }) as maplibregl.Map

    // Wait for map to be fully loaded using MapLibre's 'load' event
    await new Promise<void>(resolve => {
      if (this.map.loaded()) {
        resolve()
      } else {
        this.map.once('load', () => resolve())
      }
    });

    await this.addTerraDrawFunctionality()

    if (this.hasInitialGeoJsonValue && this.initialGeoJsonValue) {
      await this.loadExistingPolygonForEditing()
    }
  }

  private async loadExistingPolygonForEditing(): Promise<void> {
    try {
      const geoJson = JSON.parse(this.initialGeoJsonValue) as GeoJSONStoreFeatures
      terraDrawService.loadFeatures([geoJson])
      terraDrawService.startSelectMode()

      if (geoJson.id != null) {
        terraDrawService.selectFeature(geoJson.id)
      } else {
        const loaded = terraDrawService.getFirstPolygonFeature()
        if (loaded?.id != null) {
          terraDrawService.selectFeature(loaded.id)
        }
      }

      const path = MapInitializationService.geoJsonToPath(this.initialGeoJsonValue)
      if (path && path.length > 0) {
        await PolygonService.fitMapToBounds(mapService, this.map, path)
      }
    } catch (error) {
      console.error('Failed to load garden area for editing:', error)
      this.showErrorMessage('Failed to load garden area for editing. Please refresh the page.')
    }
  }

  private async addTerraDrawFunctionality(): Promise<void> {
    try {
      // Ensure map is properly initialized
      if (!this.map) {
        throw new Error('Map not initialized')
      }

      // Wait for map to be idle (all tiles loaded, animations complete)
      await new Promise<void>(resolve => {
        if (this.map.isStyleLoaded() && this.map.areTilesLoaded()) {
          resolve()
        } else {
          this.map.once('idle', () => resolve())
        }
      });

      // Initialize TerraDraw with MapLibre map
      await terraDrawService.initialize(this.map)

      // Start TerraDraw in polygon mode (switched to select when loading existing geometry)
      await terraDrawService.start()

      // finish fires for both initial draw and subsequent vertex/feature edits
      terraDrawService.on('finish', this.boundFinishHandler)

    } catch (error) {
      console.error('Failed to add TerraDraw functionality:', error)
      this.showErrorMessage('Failed to initialize drawing tools. Please refresh the page.')
    }
  }

  private async handlePolygonFinish(featureId: string | number): Promise<void> {
    try {
      // Get the completed polygon feature as GeoJson
      const polygonFeature = terraDrawService.getFirstPolygonFeature()

      if (polygonFeature) {
        // Save GeoJson to backend
        this.saveGeoJsonToBackend(polygonFeature)
        // Allow corner dragging without a full redraw
        terraDrawService.startSelectMode()
        terraDrawService.selectFeature(featureId)
      } else {
        console.warn('No polygon feature found after completion')
      }
    } catch (error) {
      console.error('Error handling polygon completion:', error)
    }
  }

  // Clear an in-progress drawing and restart polygon mode (does not hit the backend)
  clearInProgressDrawing(): void {
    try {
      terraDrawService.clearFeatures()
      terraDrawService.startPolygonMode()
    } catch (error) {
      console.error('Error clearing in-progress garden area:', error)
    }
  }

  private async saveGeoJsonToBackend(geoJSON: Feature | null): Promise<void> {
    if (!this.hasGardenIdValue) return

    // Use Turbo Streams for a cleaner approach
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/gardens/${this.gardenIdValue}/property_boundary`
    form.setAttribute('data-turbo-stream', 'true')

    // Add the GeoJson data
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'property_boundary'
    input.value = geoJSON ? JSON.stringify(geoJSON) : ''
    form.appendChild(input)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Submit the form
    document.body.appendChild(form)
    form.requestSubmit()
    document.body.removeChild(form)
  }

  override disconnect(): void {
    // Clean up TerraDraw when controller is disconnected
    if (terraDrawService) {
      terraDrawService.off('finish', this.boundFinishHandler)
      terraDrawService.stop()
    }
  }

  private showErrorMessage(message: string): void {
    errorHandler.showError(message)
  }
}
